'use client';

// A lightweight, opinionated wrapper around @headlessui/react `Menu` primitives.
// It exposes the raw primitives (Menu*, MenuItem, …) **and** adds a convenient
// `Dropdown` component with sensible Tailwind-based defaults so teams can either
// compose their own menu system or use the out-of-the-box experience.

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';

/* -------------------------------------------------------------------------- */
/*                               Re-exports                                   */
/* -------------------------------------------------------------------------- */
// Give callers the option to build their own menu system completely from the
// low-level primitives. This fulfils the “expose the raw building blocks” part
// of the user request.
export {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuSeparator,
} from '@headlessui/react';

/* -------------------------------------------------------------------------- */
/*                         Convenience «Dropdown» API                         */
/* -------------------------------------------------------------------------- */
// There are four public sub-components:
// 1. Dropdown.Root   – the provider (aka ⟨Menu⟩)
// 2. Dropdown.Button – the trigger
// 3. Dropdown.Items  – the pop-over container
// 4. Dropdown.Item   – an individual entry
// 5. Dropdown.Separator – a horizontal rule divider
//
// These defaults aim for a compact, purple-accented visual style that matches
// other Finch v3 UI elements.  All classNames can be overridden/extended via
// the customary `className` prop.

/* ---------------------------------- Root ---------------------------------- */
const Root: React.FC<Headless.MenuProps> = (props) => {
  return <Headless.Menu {...props} />;
};

/* -------------------------------- Button ---------------------------------- */
export type ColorVariant = 'purple' | 'green' | 'blue' | 'red' | 'gray';

const VARIANT_CLASSES: Record<ColorVariant, string> = {
  purple:
    'bg-purple-100 text-purple-700 hover:bg-purple-200 focus-visible:ring-purple-500',
  green:
    'bg-green-100 text-green-700 hover:bg-green-200 focus-visible:ring-green-500',
  blue: 'bg-blue-100 text-blue-700 hover:bg-blue-200 focus-visible:ring-blue-500',
  red: 'bg-red-100 text-red-700 hover:bg-red-200 focus-visible:ring-red-500',
  gray: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus-visible:ring-gray-500',
};

interface DropdownButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  as?: React.ElementType;
  className?: string;
  variant?: ColorVariant;
}

const Button: React.FC<DropdownButtonProps> = ({
  as: Component = 'button',
  variant = 'purple',
  className,
  ...rest
}) => {
  return (
    <Headless.MenuButton
      as={Component}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-sm font-medium',
        'focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60 disabled:pointer-events-none',
        VARIANT_CLASSES[variant],
        className,
      )}
      {...rest}
    />
  );
};

/* --------------------------------- Items ---------------------------------- */
interface ItemsProps extends Omit<Headless.MenuItemsProps, 'className'> {
  className?: string;
}

const Items: React.FC<ItemsProps> = ({
  className,
  anchor = 'bottom',
  ...rest
}) => {
  return (
    <Headless.MenuItems
      anchor={anchor}
      className={clsx(
        // Positioning handled by HUI using the `anchor` prop
        'z-50 mt-2 min-w-40 origin-top-right rounded-md bg-white shadow-lg',
        'ring-1 ring-black/5 focus:outline-none',
        'dark:bg-zinc-800 dark:ring-white/10',
        className,
      )}
      {...rest}
    />
  );
};

/* ---------------------------------- Item ---------------------------------- */
type ItemRenderProps = {
  active: boolean;
  disabled: boolean;
};

interface DropdownItemProps {
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode | ((props: ItemRenderProps) => React.ReactNode);
}

const Item: React.FC<DropdownItemProps> = ({
  className,
  disabled,
  children,
  ...buttonProps
}) => (
  <Headless.MenuItem disabled={disabled} as="div">
    {({ active, disabled: isDisabled }) => {
      const base =
        'flex w-full cursor-pointer items-center gap-2 rounded-sm px-4 py-2 text-sm';
      const activeStyles = 'bg-gray-100 text-gray-900 dark:bg-gray-800/50';
      const disabledStyles = 'opacity-50 cursor-not-allowed';

      return (
        <button
          type="button"
          disabled={isDisabled}
          className={clsx(
            base,
            active && !isDisabled && activeStyles,
            isDisabled && disabledStyles,
            className,
          )}
          {...buttonProps}
        >
          {typeof children === 'function'
            ? children({ active, disabled: isDisabled })
            : children}
        </button>
      );
    }}
  </Headless.MenuItem>
);

/* ------------------------------ Separator --------------------------------- */
interface SeparatorProps extends Headless.MenuSeparatorProps {
  className?: string;
}

const Separator: React.FC<SeparatorProps> = ({ className, ...rest }) => (
  <Headless.MenuSeparator
    className={clsx('my-1 h-px bg-gray-200 dark:bg-zinc-700', className)}
    {...rest}
  />
);

/* ----------------------------- Export shape ------------------------------- */
export const Dropdown = Object.assign(Root, {
  Button,
  Items,
  Item,
  Separator,
});

// Individual named exports for tree-shaking / selective imports if desired
export {
  Button as DropdownButton,
  Item as DropdownItem,
  Items as DropdownItems,
  Separator as DropdownSeparator,
};
